/**
 * Invoice Detail Sheet (Account Tab)
 *
 * Slide-out panel showing full invoice details + line items when a row is clicked.
 * Mirrors QuoteDetailSheet for visual consistency between quote and invoice.
 */

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { useInvoice } from '@/features/invoices/hooks/useInvoices';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { InvoiceDetail } from '../types';
import type { InvoiceSku } from '@/features/invoices/types';

interface InvoiceDetailSheetProps {
  invoice: InvoiceDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_VARIANT: Record<string, 'secondary' | 'warning' | 'success' | 'outline' | 'destructive'> = {
  preparation: 'secondary',
  sent: 'warning',
  paid: 'success',
  cancelled: 'outline',
  void: 'destructive',
  expired: 'outline',
};

const STATUS_LABELS: Record<string, string> = {
  preparation: 'Preparation',
  sent: 'Sent',
  paid: 'Paid',
  cancelled: 'Cancelled',
  void: 'Void',
  expired: 'Expired',
};

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between py-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right">{value || '—'}</span>
    </div>
  );
}

function LineItemsTable({ items, currency }: { items: InvoiceSku[]; currency: string }) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground py-2">No line items</p>;
  }

  return (
    <div className="border rounded-md overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50%]">Item</TableHead>
            <TableHead className="text-right">Qty</TableHead>
            <TableHead className="text-right">Unit Price</TableHead>
            <TableHead className="text-right">Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item, i) => {
            const qty = Number(item.quantity) || 0;
            const up = Number(item.unit_price) || 0;
            const disc = Number(item.discount) || 0;
            const net = up * (1 - disc / 100);
            const total = Number(item.total) || qty * net;
            return (
              <TableRow key={item.ref_id || `${item.sku}-${i}`}>
                <TableCell className="py-2">
                  <p className="text-sm font-medium">{item.sku || '—'}</p>
                  {item.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {item.description}
                    </p>
                  )}
                  {disc > 0 && (
                    <p className="text-xs text-muted-foreground">Discount: {disc}%</p>
                  )}
                </TableCell>
                <TableCell className="text-right py-2 tabular-nums">{qty}</TableCell>
                <TableCell className="text-right py-2 tabular-nums">
                  {formatCurrency(up, currency)}
                </TableCell>
                <TableCell className="text-right py-2 tabular-nums font-medium">
                  {formatCurrency(total, currency)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default function InvoiceDetailSheet({ invoice, open, onOpenChange }: InvoiceDetailSheetProps) {
  // Prefer mongoInvoiceId (V3 returns skus); fall back to PG UUID.
  const fetchId = invoice ? (invoice.mongoInvoiceId || invoice.id) : undefined;
  const { data: fullInvoice, isLoading } = useInvoice(open ? fetchId : undefined);

  if (!invoice) return null;

  const currency = fullInvoice?.currency || invoice.currency || 'CAD';
  const subtotal = fullInvoice?.subtotal_amount ?? parseFloat(invoice.subtotal);
  const tax = fullInvoice?.tax_amount ?? parseFloat(invoice.taxAmount);
  const total = fullInvoice?.total ?? parseFloat(invoice.total);
  const skus = fullInvoice?.skus ?? [];

  const projectName = fullInvoice?.project_name;
  const poNumber = fullInvoice?.po_number;
  const paymentTerms = fullInvoice?.payment_terms;
  const salesManager = fullInvoice?.sales_manager && typeof fullInvoice.sales_manager === 'object'
    ? `${fullInvoice.sales_manager.firstname || ''} ${fullInvoice.sales_manager.lastname || ''}`.trim()
    : null;
  const contractualManager = fullInvoice?.contractual_manager && typeof fullInvoice.contractual_manager === 'object'
    ? `${fullInvoice.contractual_manager.firstname || ''} ${fullInvoice.contractual_manager.lastname || ''}`.trim()
    : null;
  const contractName = fullInvoice?.contract && typeof fullInvoice.contract === 'object'
    ? (fullInvoice.contract.name || fullInvoice.contract.contract_number)
    : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3">
            <span className="font-mono">Invoice #{invoice.invoiceNumber}</span>
            <Badge variant={STATUS_VARIANT[invoice.status] || 'secondary'}>
              {STATUS_LABELS[invoice.status] || invoice.status}
            </Badge>
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {projectName && (
            <div>
              <h4 className="text-sm font-medium mb-1 text-muted-foreground">Project</h4>
              <p className="text-sm font-medium">{projectName}</p>
            </div>
          )}

          <div>
            <h4 className="text-sm font-medium mb-1 text-muted-foreground">Amount</h4>
            <p className="text-2xl font-semibold">{formatCurrency(total, currency)}</p>
          </div>

          <div>
            <h4 className="text-sm font-medium mb-2 text-muted-foreground">Dates</h4>
            <DetailRow label="Issue Date" value={invoice.invoiceDate ? formatDate(invoice.invoiceDate) : null} />
            <DetailRow label="Due Date" value={invoice.dueDate ? formatDate(invoice.dueDate) : null} />
            <DetailRow label="Paid" value={invoice.paymentDate ? formatDate(invoice.paymentDate) : null} />
          </div>

          <div>
            <h4 className="text-sm font-medium mb-2 text-muted-foreground">Financial</h4>
            <DetailRow label="Subtotal" value={formatCurrency(subtotal, currency)} />
            <DetailRow label="Tax" value={formatCurrency(tax, currency)} />
            <Separator className="my-1" />
            <DetailRow
              label="Total"
              value={<span className="font-semibold">{formatCurrency(total, currency)}</span>}
            />
          </div>

          {(salesManager || contractualManager || poNumber || paymentTerms || contractName) && (
            <div>
              <h4 className="text-sm font-medium mb-2 text-muted-foreground">Details</h4>
              {salesManager && <DetailRow label="Sales Manager" value={salesManager} />}
              {contractualManager && <DetailRow label="Contractual Manager" value={contractualManager} />}
              {contractName && <DetailRow label="Contract" value={contractName} />}
              {poNumber && <DetailRow label="PO Number" value={poNumber} />}
              {paymentTerms && <DetailRow label="Payment Terms" value={paymentTerms} />}
            </div>
          )}

          <div>
            <h4 className="text-sm font-medium mb-2 text-muted-foreground">Line Items</h4>
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : (
              <LineItemsTable items={skus} currency={currency} />
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
